import React, { Component } from 'react';
import { Route } from 'react-router-dom';

// Adds a transitionClass prop to track transition fades in/out, with values
//   of "transition-in" and "transition-out" by default.
// Mounts and transitions based on passed React Router path and shouldMountFn.

const EXITED = 'exited'; // Unmounted
const ENTERING = 'entering'; // Mounted, out
const ENTERED = 'entered'; // Mounted, in
const EXITING = 'exiting'; // Mounted, out
const TRANSITION_IN = 'in';
const TRANSITION_OUT = 'out';

const REFLOW_DELAY_MS = 20;

const withTransitionRoute =
  ({
    pathFn,
    exact = false,
    shouldMountFn = undefined,
    classPrefix = 'transition-',
    duration = 1000,
    keyFn = undefined
  }) =>
  BaseComponent => {
    function WithTransitionRoute(props) {
      const path = pathFn(props);
      return (
        <Route
          path={path}
          exact={exact}
          children={({ match }) => (
            <WithTransitionClass match={match} {...props} />
          )}
        />
      );
    }

    class WithTransitionClass extends Component {
      constructor(props) {
        super(props);

        this.state = {
          transitionState: EXITED,
          key: keyFn && keyFn(props)
        };

        this.timeoutId = null;
      }

      componentWillReceiveProps(next) {
        const trans = this.state.transitionState;
        const shouldMount =
          next.match && (!shouldMountFn || shouldMountFn(next));
        const key = this.state.key;
        const nextKey = shouldMount ? keyFn && keyFn(next) : key;
        const willMount = shouldMount && (key === nextKey || trans === EXITED);

        if (willMount && (trans === EXITED || trans === EXITING)) {
          const newState = { transitionState: ENTERING };
          if (key !== nextKey) {
            newState.key = nextKey;
          }
          this.setState(newState);
          this.setStateTimeout(ENTERED, REFLOW_DELAY_MS);
        } else if (!willMount && (trans === ENTERED || trans === ENTERING)) {
          this.setState({ transitionState: EXITING });
          this.setStateTimeout(EXITED, duration);
        }
      }

      componentWillUnmount() {
        clearTimeout(this.timeoutId);
      }

      setStateTimeout(newState, timeoutMs) {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
          this.setState({ transitionState: newState });
          this.timeoutId = null;
        }, timeoutMs);
      }

      render() {
        const { transitionState: trans, key } = this.state;

        if (trans === EXITED) {
          return null;
        }
        const { match, ...otherProps } = this.props;
        const transClass = trans === ENTERED ? TRANSITION_IN : TRANSITION_OUT;
        const transProps = { transitionClass: classPrefix + transClass, key };
        return <BaseComponent {...otherProps} {...transProps} />;
      }
    }

    return WithTransitionRoute;
  };

export default withTransitionRoute;
